import { DeepPartial } from 'react-hook-form';
import {
  ContainerEntry,
  ContainerOrder,
  ItemTypeEntry,
} from './OrderFormContext';
import { EventMerchantOrderSharedLinkInfoQuery } from '@/graphql/queries/eventMerchantOrderSharedLinkInfo.generated';
import {
  EventPriceKind,
  ItemTypeCategory,
  MerchantOrderItemUnit,
} from '@/graphql/generatedTypes';

/**
 * The item type categories an order form may carry. Assets are buffer-order-only
 * (colugo rejects them on event account orders), store assets are store-only.
 */
export const ORDER_FORM_ITEM_TYPE_CATEGORIES: ReadonlySet<ItemTypeCategory> =
  new Set([ItemTypeCategory.ContainerStation]);

/**
 * One fresh (quantity 0) item type line per allowed type, cloned so each item
 * location keeps its own quantities.
 */
export function buildItemTypeEntries(
  allowedItemTypes:
    | ReadonlyArray<{
        id: number;
        name?: string | null;
        image_url?: string | null;
        color_hex_code?: string | null;
        category: ItemTypeCategory;
      }>
    | null
    | undefined,
  prices?: Record<number, ItemTypeEntry['price']>,
): Record<number, ItemTypeEntry> {
  const entries: Record<number, ItemTypeEntry> = {};
  (allowedItemTypes ?? [])
    .filter((it) => ORDER_FORM_ITEM_TYPE_CATEGORIES.has(it.category))
    .forEach((it) => {
      entries[it.id] = {
        info: {
          id: it.id,
          name: it.name ?? `#${it.id}`,
          imageUrl: it.image_url,
          color: it.color_hex_code,
          category: it.category,
        },
        quantity: 0,
        price: prices?.[it.id],
      };
    });
  return entries;
}

export function cloneItemTypeEntries(
  entries: Record<number, ItemTypeEntry>,
): Record<number, ItemTypeEntry> {
  const clone: Record<number, ItemTypeEntry> = {};
  Object.entries(entries).forEach(([id, entry]) => {
    clone[Number(id)] = { ...entry, info: { ...entry.info } };
  });
  return clone;
}

/**
 * Recursively makes all properties optional and allows them to be null or undefined.
 * Works with nested objects and arrays.
 */
export type DeepPartialNullable<T> = T extends object
  ? T extends Array<infer U>
    ? Array<DeepPartialNullable<U> | null | undefined>
    : { [K in keyof T]?: DeepPartialNullable<T[K]> | null | undefined }
  : T | null | undefined;

/**
 * Deeply merges partial values (possibly with `undefined` or `null`)
 * into default values, skipping only `undefined` fields but keeping valid `null`s.
 *
 * Ideal for use with `reset()` in React Hook Form.
 */
export function safeReset<T extends object>(
  resetFn: (values: T) => void,
  defaults: T,
  partial?: DeepPartialNullable<T> | null,
): void {
  if (!partial) {
    resetFn(defaults);
    return;
  }

  const deepMergeSkipUndefined = <U>(
    base: U,
    patch: DeepPartialNullable<U> | null | undefined,
  ): U => {
    // Case: no patch at all
    if (
      patch === undefined ||
      patch === null ||
      (typeof patch === 'string' && patch === '')
    ) {
      return base;
    }

    // Arrays
    if (Array.isArray(base) && Array.isArray(patch)) {
      return patch.map((v, i) =>
        deepMergeSkipUndefined(base[i], v),
      ) as unknown as U;
    }

    // Objects
    if (isPlainObject(base) && isPlainObject(patch)) {
      const result: Record<string, unknown> = { ...base };

      for (const key of Object.keys(patch) as Array<keyof typeof patch>) {
        const patchValue = patch[key];
        if (patchValue === undefined) continue;
        const baseValue = (base as Record<string, unknown>)[key as string];
        result[key as string] =
          isPlainObject(patchValue) || Array.isArray(patchValue)
            ? deepMergeSkipUndefined(
                baseValue,
                patchValue as DeepPartial<object>,
              )
            : patchValue;
      }

      return result as U;
    }

    // Primitives / mismatched types
    return patch as U;
  };

  const merged = deepMergeSkipUndefined(defaults, partial);
  resetFn(merged);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export const getContainerOrderFromRemoteFormData = (
  f: EventMerchantOrderSharedLinkInfoQuery['eventMerchantOrderSharedLinkInfo'],
) => {
  const containerOrder: ContainerOrder = {};
  const containers: Record<number, ContainerEntry> = {};
  const containerPrices: Record<number, ContainerEntry['price']> = {};
  const itemTypePrices: Record<number, ItemTypeEntry['price']> = {};

  f.prices?.forEach((c) => {
    const amounts = {
      cleaningPrice: c.cleaning_price ?? 0,
      depositPrice: c.deposit_price ?? 0,
      lostPrice: c.lost_price ?? 0,
      rentalPrice: c.rental_price ?? 0,
    };
    if (c.kind === EventPriceKind.ContainerType) {
      containerPrices[c.container_type_id as number] = amounts;
    } else {
      itemTypePrices[c.item_type_id as number] = amounts;
    }
  });

  f.event?.allowed_container_types.forEach((c) => {
    containers[c.id] = {
      info: {
        id: c.id,
        color: c.color_hex_code,
        name: c.name,
        imageUrl: c.image_url,
        segment: c.segment ?? '',
        packingUnit: c.packing_unit ?? 0,
        sizeHelper: c.size_helper ?? '',
      },
      price: containerPrices[c.id],
      quantity: 0,
      unit: MerchantOrderItemUnit.Boxes,
    };
  });

  const defaultContainers = (f.event?.allowed_container_types ?? []).reduce(
    (acc, curr) => {
      if (!containers[curr.id]) {
        return acc;
      }
      const priceClone = containers[curr.id].price
        ? { ...containers[curr.id].price }
        : containers[curr.id].price;
      // Clone so each item location gets its own container entry
      acc[curr.id] = {
        ...containers[curr.id],
        info: { ...containers[curr.id].info },
        price: priceClone,
      };
      return acc;
    },
    {} as Record<number, ContainerEntry>,
  );

  const defaultItemTypes = buildItemTypeEntries(
    f.event?.allowed_item_types,
    itemTypePrices,
  );

  f.event_account?.item_locations.forEach((iL) => {
    containerOrder[iL.id] = containerOrder[iL.id] || {
      itemLocationId: iL.id,
      itemLocationName: iL.name,
      // Each location needs its own copy to avoid shared quantities
      containers: Object.entries(defaultContainers).reduce(
        (acc, [containerTypeId, container]) => {
          const priceClone = container.price
            ? { ...container.price }
            : container.price;
          acc[Number(containerTypeId)] = {
            ...container,
            info: { ...container.info },
            price: priceClone,
          };
          return acc;
        },
        {} as Record<number, ContainerEntry>,
      ),
      itemTypes: cloneItemTypeEntries(defaultItemTypes),
    };
  });

  // All item locations now have the default containers
  // Apply the quantities from the latest revision if exists
  if (Array.isArray(f.ops_order?.latest_revision?.containers)) {
    f.ops_order?.latest_revision?.containers.forEach((i) => {
      if (
        !i.item_location_id ||
        !containerOrder[i.item_location_id]?.containers[i.type_id]
      ) {
        return;
      }
      containerOrder[i.item_location_id].containers[i.type_id].quantity =
        i.amount_int;
      containerOrder[i.item_location_id].containers[i.type_id].unit = i.unit;
    });
  }
  // Item type lines whose type the event no longer allows are not shown; the
  // backend keeps them on the order (grandfathered) until the form is resent.
  if (Array.isArray(f.ops_order?.latest_revision?.items)) {
    f.ops_order?.latest_revision?.items.forEach((i) => {
      const line =
        i.item_location_id &&
        containerOrder[i.item_location_id]?.itemTypes[i.item_type_id];
      if (line) {
        line.quantity = i.amount_int;
      }
    });
  }

  return containerOrder;
};

export const getHasOrderChanged = (
  newOrder: ContainerOrder,
  oldOrder: ContainerOrder | undefined,
) => {
  if (!oldOrder || Object.keys(newOrder).length === 0) {
    return false;
  }
  // Each item location needs to have the exact same amount of containers for each container type
  const itemLocationIds = Object.keys(newOrder);
  for (const itemLocationId of itemLocationIds) {
    const containers = newOrder[itemLocationId].containers;
    const containerTypeIds = Object.keys(containers);
    for (const containerTypeId of containerTypeIds) {
      const quantity = containers[containerTypeId].quantity;
      // In remote order, the container type won't exist if the quantity is 0
      const remoteQuantity =
        oldOrder?.[itemLocationId]?.containers[containerTypeId]?.quantity ?? 0;
      if (remoteQuantity && quantity !== remoteQuantity) {
        return true;
      }
    }
    const itemTypes = newOrder[itemLocationId].itemTypes ?? {};
    for (const itemTypeId of Object.keys(itemTypes)) {
      const quantity = itemTypes[itemTypeId].quantity;
      const remoteQuantity =
        oldOrder?.[itemLocationId]?.itemTypes?.[itemTypeId]?.quantity ?? 0;
      if (remoteQuantity && quantity !== remoteQuantity) {
        return true;
      }
    }
  }
  return false;
};
